import React, { useMemo } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { Flex } from "antd";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import FilePicker from "~/components/Forms/CreateDocument/component/FilePicker";
import {
  DocumentImportance,
  DocumentStatus,
  documentStatusSelectItems,
  importanceStatus_CreteDocument,
  resolveFieldsEditDisableByStatus_CreateDocument,
} from "~/components/Forms/CreateDocument/utils/utils";
import { documentCreateValidation } from "~/components/Forms/CreateDocument/validation";
import { useSelectorDataConfigurator } from "~/hooks/selectorDataConfigurator";
import { TUploadedFile } from "~/model/Instances/TUploadedFile";
import {
  Subdivision,
  useGetSubdivisionLazyQuery,
} from "~/schemas/generated/graphql";
import { Field } from "~/shared/ui/Field";

export interface IDocumentCreateForm {
  name: string;
  description: string;
  importance: DocumentImportance;
  signedBy: string;
  SubdivisionId?: string;
  status: DocumentStatus;
  filesIds: TUploadedFile[];
}

interface ICreateDocumentProps {
  formId: string;
  defaultValues: IDocumentCreateForm;
  onSubmitHandler: (data: IDocumentCreateForm) => void;
  isEdit: boolean;
  cantEdit: boolean;
  /** Название подразделения из редактируемого документа — нужно,
   *  чтобы Select показал label, если его нет среди подгруженных опций. */
  defaultSubdivisionName?: string;
}

const CreateDocument = ({
  formId,
  defaultValues,
  onSubmitHandler,
  isEdit,
  cantEdit,
  defaultSubdivisionName,
}: ICreateDocumentProps) => {
  const { t } = useTranslation();
  const { control, handleSubmit, errors } = useForm<IDocumentCreateForm>({
    defaultValues,
    resolver: yupResolver(documentCreateValidation(t, isEdit)),
  });

  const subdivisionSelect = useSelectorDataConfigurator<Subdivision>({
    queryGenerator: useGetSubdivisionLazyQuery,
    responseDataGetterString: "SubdivisionGetter.get.items",
    searchConfig: (searchText) => ({ name: searchText }),
  });

  // Подразделение редактируемого документа может отсутствовать
  // на первой странице опций — добавляем его в список вручную
  const subdivisionOptions = useMemo(() => {
    const options = subdivisionSelect.rowsForSelect;
    if (
      defaultValues.SubdivisionId &&
      !options.some((option) => option.value === defaultValues.SubdivisionId)
    ) {
      return [
        {
          value: defaultValues.SubdivisionId,
          label: defaultSubdivisionName || String(defaultValues.SubdivisionId),
        },
        ...options,
      ];
    }
    return options;
  }, [
    subdivisionSelect.rowsForSelect,
    defaultValues.SubdivisionId,
    defaultSubdivisionName,
  ]);

  const disabledFields = isEdit
    ? resolveFieldsEditDisableByStatus_CreateDocument(defaultValues.status) ||
      []
    : [];
  const isDisabled = (field: string) =>
    isEdit && (cantEdit || disabledFields.includes(field));

  return (
    <form id={formId} onSubmit={handleSubmit(onSubmitHandler)}>
      <Flex vertical gap={10}>
        <Field
          type="input"
          name="name"
          control={control}
          label={t("documents.card.name")}
          error={errors["name"]?.message}
          disabled={isDisabled("name")}
        />
        <Field
          type="textarea"
          name="description"
          control={control}
          label={t("documents.card.description")}
          error={errors["description"]?.message}
          disabled={isDisabled("description")}
        />
        <Field
          type="select"
          name="importance"
          control={control}
          label={t("documents.card.importance")}
          options={importanceStatus_CreteDocument}
          error={errors["importance"]?.message}
          disabled={isDisabled("importance")}
        />
        <Field
          type="input"
          name="signedBy"
          control={control}
          label={t("documents.card.signedBy")}
          error={errors["signedBy"]?.message}
          disabled={isDisabled("signedBy")}
        />
        <Field
          type="select"
          name="SubdivisionId"
          control={control}
          label={t("documents.card.subdivision")}
          options={subdivisionOptions}
          showSearch
          filterOption={false}
          onSearch={subdivisionSelect.searchValue}
          onPopupScroll={subdivisionSelect.introspectionHandler}
          error={errors["SubdivisionId"]?.message}
          disabled={isDisabled("SubdivisionId")}
        />
        {!isEdit && (
          <Field
            type="select"
            name="status"
            control={control}
            label={t("documents.card.status")}
            options={documentStatusSelectItems}
            error={errors["status"]?.message}
            disabled
          />
        )}
        <FilePicker
          isEdit={isEdit}
          label={t("documents.table.Files")}
          control={control}
          name={"filesIds"}
          error={(errors["filesIds"] as any)?.message}
          disabled={isDisabled("filesIds")}
        />
      </Flex>
    </form>
  );
};

export default CreateDocument;
